import { createSelector } from '@reduxjs/toolkit';

import { SONAR_SECTIONS, SORT_ORDER, VIEWS } from '../../constants';
import {
  filterFocusAndVisibleEntries,
  filterVisibleEntries,
  getAverages,
  getCompetitorTypeAndVisibilityFilter,
} from './utils';
import { SONAR_DATA_KEYS } from '../../utils/chart';

const { NARROW, BROAD, ALL, GROUPS, FOCUS } = VIEWS;
const { BRAND } = SONAR_SECTIONS;

// * SELECTORS
export const sonarSelector = (state) => state.sonarDashboard;
export const sonarDomainsSelector = (state) => state.sonarDashboard.domainEntries;
export const sonarNewDomainsSelector = (state) => state.sonarDashboard.newDomainEntries;
export const sonarGroupsSelector = (state) => state.sonarDashboard.groupEntries;
export const sonarSectionsSelector = (state) => state.sonarDashboard.sections;

// * SELECTORS WITH COMBINERS
export const selectSonarTableEntries = createSelector(sonarSelector, ({ sonarTable, domainEntries }) => {
  const { searchQuery, sort } = sonarTable;
  const { key, order } = sort;

  const filteredEntries = domainEntries.filter(({ domain, companyName }) => {
    if (companyName) {
      return companyName.toLowerCase().includes(searchQuery.toLowerCase());
    }

    return domain.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return filteredEntries.slice().sort((data0, data1) => {
    const score0 = data0[key]?.score;
    const score1 = data1[key]?.score;
    return order === SORT_ORDER.ASC ? score0 - score1 : score1 - score0;
  });
});

export const getSonarPowerrankData = (domainEntries) => {
  const avgDto = {
    domain: 'AVG',
    label: 'AVG',
    companyLogo: null,
    powerrank: getAverages(domainEntries),
  };

  const dtoList = filterVisibleEntries(domainEntries).sort(
    (dto0, dto1) => dto1[SONAR_DATA_KEYS.POWERRANK].score - dto0[SONAR_DATA_KEYS.POWERRANK].score,
  );

  const topList = dtoList.slice(0, 5);
  const lowList = dtoList.slice(dtoList.length - 5, dtoList.length);
  const middleList = dtoList.slice(6, dtoList.length - 5);
  const middleInFocusUpperList = middleList.filter(
    (dto) => dto.inFocus && dto[SONAR_DATA_KEYS.POWERRANK].score >= avgDto[SONAR_DATA_KEYS.POWERRANK].score,
  );
  const middleInFocusLowerList = middleList.filter(
    (dto) => dto.inFocus && dto[SONAR_DATA_KEYS.POWERRANK].score < avgDto[SONAR_DATA_KEYS.POWERRANK].score,
  );
  const finalList = [...topList, ...middleInFocusUpperList, avgDto, ...middleInFocusLowerList, ...lowList];

  return finalList;
};

export const selectDashboardEntries = createSelector(sonarDomainsSelector, (domainEntries) => {
  const focusedActiveDomains = domainEntries.filter(({ inFocus, isActive }) => inFocus && isActive);

  if (focusedActiveDomains.length >= 10) {
    return focusedActiveDomains;
  }

  const sortedDomains = [...domainEntries]
    .sort((a, b) => b.inFocus - a.inFocus || b.isActive - a.isActive || b.rank - a.rank)
    .filter((domain) => domain.inFocus && domain.isActive)
    .concat(domainEntries.filter((domain) => domain.inFocus && !domain.isActive))
    .concat(domainEntries.filter((domain) => !domain.inFocus && domain.isActive))
    .concat(domainEntries.filter((domain) => !domain.inFocus && !domain.isActive))
    .slice(0, 10);

  return sortedDomains;
});

export const selectIsAllSelected = createSelector(sonarSelector, ({ domainEntries }) =>
  domainEntries.every(({ selected }) => selected),
);

export const selectIsAnySelected = createSelector(sonarSelector, ({ domainEntries }) =>
  domainEntries.some(({ selected }) => selected),
);

export const selectChosenDomainEntry = createSelector(sonarSelector, ({ sections, domainEntries }) =>
  domainEntries.find((entry) => entry.domain === sections[BRAND].domain),
);

const viewSelectors = {
  [NARROW]: createSelector(sonarDomainsSelector, getCompetitorTypeAndVisibilityFilter(NARROW)),
  [BROAD]: createSelector(sonarDomainsSelector, getCompetitorTypeAndVisibilityFilter(BROAD)),
  [ALL]: createSelector(sonarDomainsSelector, getCompetitorTypeAndVisibilityFilter(ALL)),
  [GROUPS]: createSelector(sonarGroupsSelector, filterVisibleEntries),
  [FOCUS]: createSelector(sonarDomainsSelector, filterFocusAndVisibleEntries),
};

export const getTopAndVisibleDomains = (number, sort) => {
  return createSelector(sonarDomainsSelector, (domainEntries) =>
    filterVisibleEntries(domainEntries.slice().sort(sort).slice(0, number)),
  );
};

export const getViewForChart = (section, chartIndex) => {
  return createSelector(sonarSectionsSelector, (sections) => sections[section].charts[chartIndex].view);
};

export const getViewGroupForChart = (section, chartIndex) => {
  return createSelector(sonarSectionsSelector, (sections) => sections[section].charts[chartIndex].group);
};

export const getVisibleEntriesForView = (view, group) => {
  if (!!group && view === GROUPS && group !== 'ALL') {
    return getVisibleEntriesForGroup(group);
  }
  return viewSelectors[view] || viewSelectors[ALL];
};

const getVisibleEntriesForGroup = (group) => {
  return createSelector(sonarDomainsSelector, (domainEntries) =>
    filterVisibleEntries(domainEntries.filter(({ groups }) => groups.includes(group))),
  );
};
